import React, { useContext, useEffect, useState } from 'react'
import { Table, Button, Dropdown, Typography, Affix, Form, Modal, App } from 'antd'
import { isMobileOnly, isDesktop } from "react-device-detect"
import _ from "lodash"
import { FaPlus, FaEllipsisH } from "react-icons/fa"
import { API, Enum } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import AxiosContext from "../../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'

// Components
import CURecord from './cu-record'

const { Pages: { BasicInformation: { Province: ProvinceLanguage } }, Share, Message: MessageLanguage } = language
const {  BASIC_INFORMATION_PROVINCE_PAGINATE, BASIC_INFORMATION_PROVINCE_REMOVE } = API
const { Message } = Enum

interface ProvinceType {
    row: number,
    id: string,
    key: string,
    name: string,
    createdAt: Date,
    updatedAt: Date,
}

export default function Index() {

    const [, forceUpdate] = useState({})
    const [form] = Form.useForm()
    const [provinces, setProvinces] = useState<ProvinceType[]>([])
    const [record, setRecord] = useState<ProvinceType|null>(null)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    });
    const { axios } = useContext(AxiosContext)
    const { message, modal } = App.useApp();

    const columns: ColumnsType<ProvinceType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            title: ProvinceLanguage.name,
            dataIndex: 'name',
            key: 'name',
            sorter: true,
        },
        {
            title: Share.operation,
            dataIndex: "operation",
            key: "operation",
            render: (text, record) =>  <Dropdown menu={{ items: [
                    {
                        key: 'edit',
                        label: <Typography.Link onClick={() => {
                            setRecord(record)
                            setIsModalOpen(true)
                        }}>{ Share.edit }</Typography.Link>,
                    },
                    {
                        key: 'delete',
                        label: <Typography.Link onClick={() => modal.confirm({
                            title: Share.deleteConfirmTitle,
                            okText: Share.deleteConfirmOk,
                            okType: 'danger',
                            cancelText: Share.deleteConfirmCancel,
                            onOk() {
                                setLoadingRecords(true)
                                axios.delete(BASIC_INFORMATION_PROVINCE_REMOVE + record.id).then((response: {}) => {
                                    const { total = 1, pageSize = 1, current = 1 } = pagination
                                    const params = {
                                        page: total % pageSize !== 1 ? current : current - 1,
                                        limit: pageSize,
                                    };
                                    getProvinces(params)
                                    message.success(MessageLanguage[Message.DELETE_RECORD]).then(response => response)
                                }).catch((error: AxiosError) => {
                                    const { response: { data } } = error;
                                    // @ts-ignore
                                    message.error(MessageLanguage[data.message]).then(response => response )
                                    setLoadingRecords(false)
                                })
                            }
                        })}>{ Share.delete }</Typography.Link>,
                    },
                ] }} placement="bottomRight"><a><FaEllipsisH /></a></Dropdown>
        }
    ]

    const getProvinces = (params: AntTableParams) => {
        setLoadingRecords(true)
        axios.post(BASIC_INFORMATION_PROVINCE_PAGINATE, null, { params }).then((response: AxiosResponsePaginate) => {
            const { data: { docs: provinces, page: current, limit: pageSize, totalDocs: total } } = response
            let row = (current - 1) * pageSize;
            setProvinces(provinces.map<ProvinceType>((province: ProvinceType) => ({ ...province, row: ++row, key: province.id })))
            setLoadingRecords(false)
            setPagination({
                ...pagination,
                current,
                pageSize,
                total,
            })
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setLoadingRecords(false)
        })
    }

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({});
        const { current, pageSize } = pagination
        getProvinces({ page: current, limit: pageSize })
    }, []);

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        getProvinces({ page: current, limit: pageSize, sort });
    };

    return (
        <>
            <Modal destroyOnClose={true} title={Share.addOrEditRecord} cancelText={Share.cancel} okText={Share.save} open={isModalOpen} onOk={() => form.submit()} onCancel={() => setIsModalOpen(false)}>
                <CURecord Form={Form} form={form} record={record} getProvinces={getProvinces} pagination={pagination} />
            </Modal>
            <Table
                columns={columns}
                dataSource={provinces}
                pagination={pagination}
                loading={loadingRecords}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
            <Affix offsetBottom={30}>
                <Button type="primary" style={{marginTop: 10}} icon={<FaPlus />} onClick={() => {
                    setRecord(null)
                    setIsModalOpen(true)
                }}>{ Share.add }</Button>
            </Affix>
        </>
    )
}
